import { isRecord } from "./model";
import type { EngineDocument } from "../scene/model";
import { assertEngineDocument } from "../scene/validation";
import { decodeDataUrl } from "../scene/resources";

export const ENGINE_EXTENSION = ".3dcd";
export interface EmbeddedResource {
  path: string;
  bytes: Uint8Array;
}
export interface EngineArchiveEntries {
  manifest: string;
  document: string;
  resources: EmbeddedResource[];
}

/** Own native format; the legacy file format has no influence on its schema. */
export function encodeEngineArchive(
  document: EngineDocument,
  createdAt: string,
): EngineArchiveEntries {
  assertEngineDocument(document);
  const resources: EmbeddedResource[] = [];
  const types: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/svg+xml": "svg",
    "image/webp": "webp",
    "image/gif": "gif",
    "audio/mpeg": "mp3",
    "audio/wav": "wav",
    "audio/ogg": "ogg",
  };
  const assets = document.assets.map((asset, index) => {
    const decoded = decodeDataUrl(asset.source);
    if (!decoded)
      return {
        id: asset.id,
        kind: asset.kind,
        location: { type: "url", url: asset.source },
      };
    const path = `resources/${index + 1}.${types[decoded.mediaType] || "bin"}`;
    resources.push({ path, bytes: decoded.bytes });
    return {
      id: asset.id,
      kind: asset.kind,
      location: { type: "file", path, mediaType: decoded.mediaType },
    };
  });
  return {
    manifest: JSON.stringify(
      {
        format: "creator-engine-pro",
        formatVersion: 1,
        schemaVersion: 1,
        createdAt,
        document: "document.json",
      },
      null,
      2,
    ),
    document: JSON.stringify({ ...document, assets }),
    resources,
  };
}

export async function decodeEngineArchive(
  manifestText: string,
  documentText: string,
  readBase64: (path: string) => Promise<string>,
): Promise<EngineDocument> {
  const manifest: unknown = JSON.parse(manifestText);
  if (
    !isRecord(manifest) ||
    manifest.format !== "creator-engine-pro" ||
    manifest.formatVersion !== 1 ||
    manifest.schemaVersion !== 1 ||
    manifest.document !== "document.json"
  )
    throw new Error("Неподдерживаемая версия файла проекта.");
  const document: unknown = JSON.parse(documentText);
  if (!isRecord(document) || !Array.isArray(document.assets))
    throw new Error("В файле проекта отсутствует описание ресурсов.");
  const assets = [];
  for (const asset of document.assets) {
    if (!isRecord(asset) || !isRecord(asset.location))
      throw new Error("Некорректный ресурс проекта.");
    const location = asset.location;
    let source: string;
    if (location.type === "url" && typeof location.url === "string")
      source = location.url;
    else if (
      location.type === "file" &&
      typeof location.path === "string" &&
      /^resources\/\d+\.[a-z0-9]+$/.test(location.path) &&
      typeof location.mediaType === "string" &&
      /^[\w.+-]+\/[\w.+-]+$/.test(location.mediaType)
    ) {
      source = `data:${location.mediaType};base64,${await readBase64(location.path)}`;
    } else throw new Error("Некорректный адрес ресурса проекта.");
    assets.push({ id: asset.id, kind: asset.kind, source });
  }
  document.assets = assets;
  assertEngineDocument(document);
  return document;
}
